import { useEffect, useRef, useState } from "react";
import { Box, Typography, Button, TextField, CircularProgress, Collapse } from "@mui/material";
import VpnKeyRoundedIcon from "@mui/icons-material/VpnKeyRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AppDialog from "./AppDialog";
import { useToast } from "../../../context/ToastContext";
import { captureLawyerSession } from "../../../services/LawyerService";
import CookieCaptureGuide from "./CookieCaptureGuide";

// Mensaje que manda la extensión de captura (content script en esta misma
// página) cuando el operador aprieta Alt+K en el portal judicial.
// Contrato: { type: "JEX_SESSION_CAPTURED", payload: { enrollment, cookies, userAgent } }
export const SESSION_BRIDGE_MESSAGE = "JEX_SESSION_CAPTURED";

// El header Cookie de un pedido HTTP es "nombre1=valor1; nombre2=valor2; ...".
function parseCookieHeader(raw) {
  return String(raw || "")
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const idx = part.indexOf("=");
      if (idx === -1) return null;
      return { name: part.slice(0, idx).trim(), value: part.slice(idx + 1).trim() };
    })
    .filter((c) => c && c.name);
}

/**
 * El servidor no puede loguear solo contra el portal judicial. Quien ve este
 * diálogo entra al portal desde su PC, y la sesión llega a jex sola (extensión
 * con Alt+K) o se pega a mano como respaldo.
 */
export default function LawyerSessionRequiredDialog({ open, onClose, lawyer, onCaptured, progressLabel }) {
  const { showToast } = useToast();
  const [cookieInput, setCookieInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showManual, setShowManual] = useState(false);

  // Reset del estado propio del diálogo — sin avisarle al padre.
  const resetInput = () => {
    setCookieInput("");
    setShowGuide(false);
    setShowManual(false);
  };

  // Cierre real (botón Cerrar, click afuera, Escape) — avisa al padre.
  const handleClose = () => {
    resetInput();
    onClose();
  };

  const saveSession = async ({ cookies, userAgent, enrollment }) => {
    if (!lawyer?.id) return;

    // .PROMETEO es el ticket de sesión real del portal.
    const hasAuthTicket = cookies.some((c) => c.name === ".PROMETEO" && c.value);
    if (!hasAuthTicket) {
      showToast(
        "Falta la cookie .PROMETEO. Volvé al portal, recargá la página (F5) y apretá Alt+K de nuevo.",
        "error"
      );
      return;
    }

    setSaving(true);
    try {
      const res = await captureLawyerSession(lawyer.id, {
        cookies,
        userAgent: userAgent || navigator.userAgent,
        enrollment: enrollment || undefined,
      });
      if (res.data?.status === "success") {
        showToast("Sesión guardada. jex ya la puede usar.", "success");
        const capturedLawyer = lawyer;
        resetInput();
        onCaptured?.(capturedLawyer);
      } else {
        showToast(res.data?.message || "No se pudo guardar la sesión.", "error");
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Error guardando la sesión.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Escucha la sesión que manda la extensión mientras el diálogo está abierto.
  const saveRef = useRef(saveSession);
  saveRef.current = saveSession;

  useEffect(() => {
    if (!open || !lawyer?.id) return undefined;

    const onMessage = (event) => {
      if (event.source !== window) return;
      const data = event.data;
      if (!data || data.type !== SESSION_BRIDGE_MESSAGE || !data.payload) return;

      const { enrollment, cookies, userAgent } = data.payload;
      const expected = String(lawyer.enrollment ?? "").trim();
      const received = String(enrollment ?? "").trim();

      if (!received) {
        showToast(
          "La sesión llegó sin matrícula, no se puede verificar a qué abogado corresponde. Usá el pegado manual.",
          "warning"
        );
        return;
      }

      if (expected && received !== expected) {
        showToast(
          `Llegó una sesión de la matrícula ${received}, pero acá se necesita la de ${lawyer.name} ${lawyer.lastname} (${expected}). No se guardó.`,
          "warning"
        );
        return;
      }

      saveRef.current({ cookies: Array.isArray(cookies) ? cookies : [], userAgent, enrollment: received });
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lawyer?.id, lawyer?.enrollment]);

  const handleSaveManual = () => {
    const cookies = parseCookieHeader(cookieInput);
    if (cookies.length === 0) {
      showToast("Pegá el valor del header Cookie primero.", "error");
      return;
    }
    saveSession({ cookies, userAgent: navigator.userAgent, enrollment: null });
  };

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title={
        progressLabel
          ? `Se necesita la sesión del abogado (${progressLabel})`
          : "Se necesita la sesión del abogado"
      }
      icon={<VpnKeyRoundedIcon />}
      maxWidth="sm"
      actions={
        <Button
          onClick={handleClose}
          color="inherit"
          sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 800, px: 2.2 }}
        >
          Cerrar
        </Button>
      }
    >
      <Typography sx={{ fontWeight: 850, lineHeight: 1.5, color: "text.primary" }}>
        {lawyer
          ? `Para continuar, necesito la sesión de ${lawyer.name} ${lawyer.lastname}${lawyer.enrollment ? ` (matrícula ${lawyer.enrollment})` : ""}.`
          : "Para continuar, necesito la sesión de este abogado."}
      </Typography>

      <Box
        sx={(theme) => ({
          mt: 1.6,
          p: 1.6,
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: "primary.main",
          bgcolor: theme.palette.mode === "dark" ? "rgba(25,118,210,0.12)" : "rgba(25,118,210,0.06)",
        })}
      >
        <Typography sx={{ fontWeight: 900, fontSize: 13.5, mb: 0.8 }}>Qué tenés que hacer</Typography>
        <Typography component="ol" sx={{ m: 0, pl: 2.4, fontSize: 13, lineHeight: 1.7 }}>
          <li>
            Abrí el portal judicial en esta misma PC y logueate con{" "}
            {lawyer ? <strong>{lawyer.name} {lawyer.lastname}</strong> : "este abogado"}.
          </li>
          <li>Entrá a <strong>Mis Expedientes</strong> y recargá la página (F5).</li>
          <li>
            Apretá <strong>Alt+K</strong>. La extensión revisa que la sesión funcione y, si está
            bien, la manda a jex sola.
          </li>
          <li>Volvé a esta pestaña de jex. Si todo salió bien, la sesión se guarda sola y la carga continúa.</li>
        </Typography>
        <Typography sx={{ mt: 1, fontSize: 12, opacity: 0.8 }}>
          Si la extensión se pone roja, no se copió nada: el cartelito dice qué falta. Repetí desde
          el paso 2.
        </Typography>
      </Box>

      <Box sx={{ mt: 1.4, display: "flex", alignItems: "center", gap: 1, fontSize: 13 }}>
        <CircularProgress size={14} />
        <Typography sx={{ fontSize: 13, opacity: 0.8 }}>
          {saving ? "Guardando la sesión…" : "Esperando la sesión desde el portal (Alt+K)…"}
        </Typography>
      </Box>

      <Box
        onClick={() => setShowManual((v) => !v)}
        sx={{
          mt: 1.4,
          display: "flex",
          alignItems: "center",
          gap: 0.6,
          cursor: "pointer",
          userSelect: "none",
          color: "text.secondary",
          fontSize: 12.5,
          fontWeight: 700,
        }}
      >
        <ExpandMoreRoundedIcon
          fontSize="small"
          sx={{ transform: showManual ? "rotate(180deg)" : "none", transition: "transform .15s" }}
        />
        {showManual ? "Ocultar pegado manual" : "No tengo la extensión: pegar la cookie a mano"}
      </Box>

      <Collapse in={showManual}>
        <Box sx={{ mt: 1.2 }}>
          <Box
            onClick={() => setShowGuide((v) => !v)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              cursor: "pointer",
              userSelect: "none",
              color: "primary.main",
              fontSize: 12.5,
              fontWeight: 800,
            }}
          >
            <ExpandMoreRoundedIcon
              fontSize="small"
              sx={{ transform: showGuide ? "rotate(180deg)" : "none", transition: "transform .15s" }}
            />
            {showGuide ? "Ocultar guía paso a paso" : "Ver guía paso a paso: dónde encontrar la cookie"}
          </Box>

          <Collapse in={showGuide}>
            <Box sx={{ mt: 1.6, mb: 0.6 }}>
              <CookieCaptureGuide />
            </Box>
          </Collapse>

          <TextField
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            placeholder="ASP.NET_SessionId=...; .PROMETEO=...; NLB_JusticiaCordoba=...; TS99ec...=..."
            value={cookieInput}
            onChange={(e) => setCookieInput(e.target.value)}
            sx={{
              mt: 1.2,
              "& .MuiInputBase-root": { fontFamily: "monospace", fontSize: 12.5 },
            }}
          />

          <Button
            onClick={handleSaveManual}
            disabled={saving || !cookieInput.trim()}
            variant="outlined"
            fullWidth
            sx={{ mt: 1, borderRadius: 2.5, textTransform: "none", fontWeight: 800 }}
          >
            Guardar sesión pegada
          </Button>
        </Box>
      </Collapse>
    </AppDialog>
  );
}
